import Error from 'next/error';
import { useRouter } from 'next/router';
import { FC, useContext, useMemo } from 'react';
import Loading from './Loading';
import { TOURNAMENT_CATEGORY } from '@/constants';
import SchoolContext from '@/contexts/school';
import TournamentContext from '@/contexts/tournament';
import useSchoolTournament from '@/hooks/useSchoolTournament';

const AuthTournament: FC = ({ children }) => {
  const router = useRouter();

  const { id, slug } = useContext(SchoolContext);
  const [{ schoolTournament }] = useSchoolTournament(router.query.tournament_id, id);

  /**
   * Check tournament category
   * @returns {number|null} - category: 1 - normal tournament, 2 - multiple tournament, 3 - sub tournament
   */
  const tournamentCategory: number | null = useMemo(() => {
    if (schoolTournament) {
      // Normal tournament
      if (!schoolTournament?.is_amateur_tournament && !schoolTournament?.amateur_tournament_id) {
        return TOURNAMENT_CATEGORY.NORMAL;
      }

      // Multiple tournament
      else if (schoolTournament.is_amateur_tournament) {
        return TOURNAMENT_CATEGORY.MULTIPLE_TOURNAMENT;
      }

      // Sub Tournament
      else {
        return TOURNAMENT_CATEGORY.SUB_TOURNAMENT;
      }
    } else {
      return null;
    }
  }, [schoolTournament]);

  /**
   * Check forbbiden access
   */
  const isForbbidenAccess = useMemo(() => {
    if (schoolTournament && tournamentCategory) {
      const path = router.asPath.split('?')[0];

      const forbbidenAccessList = {
        [TOURNAMENT_CATEGORY.SUB_TOURNAMENT]: [
          `/${slug}/tournament/${schoolTournament.id}/setting/account`,
          `/${slug}/tournament/${schoolTournament.id}/setting/court`,
          `/${slug}/tournament/${schoolTournament.id}/sub-tournament-list`,
        ],
        [TOURNAMENT_CATEGORY.MULTIPLE_TOURNAMENT]: [
          `/${slug}/tournament/${schoolTournament.id}/apply-list`,
          `/${slug}/tournament/${schoolTournament.id}/claim`,
          `/${slug}/tournament/${schoolTournament.id}`,
          `/${slug}/tournament/${schoolTournament.id}/setting/attracting-customer`,
          `/${slug}/tournament/${schoolTournament.id}/setting/basic-config`,
          `/${slug}/tournament/${schoolTournament.id}/setting/notification`,
          `/${slug}/tournament/${schoolTournament.id}/setting/help-school`,
          `/${slug}/tournament/${schoolTournament.id}/setting/sponsors`,
        ],

        [TOURNAMENT_CATEGORY.NORMAL]: [],
      };

      if (forbbidenAccessList[tournamentCategory] && forbbidenAccessList[tournamentCategory].includes(path)) {
        return true;
      }
      return false;
    }
    return true;
  }, [router.asPath, schoolTournament, slug, tournamentCategory]);

  if (router.pathname.includes('tournament') && !router.pathname.includes('tournament-list')) {
    if (schoolTournament === undefined) {
      return <Loading />;
    } else if (schoolTournament === null || isForbbidenAccess) {
      return <Error statusCode={404} title='This page could not be found'></Error>;
    }
  }

  return (
    <TournamentContext.Provider
      value={{ currentTournament: schoolTournament, currentTournamentCategory: tournamentCategory }}>
      {children}
    </TournamentContext.Provider>
  );
};

export default AuthTournament;
